import {headerConfig} from "./config";
import {getCssVariableValue} from "./scss_variables";
import {checkForCollapsibles, hasConsent} from "./utils";
import {
    appendCollapseAllButton,
    appendScrollButtons, bindToolTips, displayError, displaySuccess, generateCreditCardsHTML,
    hideCollapseButton,
    renderBillingHtml,
    setSubscribeBtnText
} from "./dom-manipulation";
import {getBillingData, getSimpleBots, getUserCards} from "../../ads/js/api";
import {fullFetch, simpleFetch} from "./api";
import urls from "./urls";
import {purchaseConfig} from "../../subscribe/js/config";
import i18next from "i18next";

const SUPPORT_CHAT_MOBILE_QUERY = '(max-width: 767.98px)';
const SUPPORT_CHAT_IFRAME_SELECTOR = 'iframe[title="chat widget"]';
const SUPPORT_CHAT_OPEN_CLASS = 'support-chat-open';
const FUNCTIONAL_COOKIE_CATEGORY_PATTERN = /(functionality|functional|функцион|функціон|funkcjonal|funcional|funcțion|funcţion|funcionais|funcionale)/i;
const FUNCTIONAL_COOKIE_PREFERENCES_RETRY_LIMIT = 12;
const FUNCTIONAL_COOKIE_PREFERENCES_RETRY_DELAY = 150;
const COOKIE_PREFERENCES_ROOT_SELECTORS = [
    '#cc-main',
    '.cc-main',
    '.cc-window',
    '.freeprivacypolicy-com---preferences-center',
    '.freeprivacypolicy-com---pc',
    '[role="dialog"][aria-modal="true"]',
    '[class*="preferences"][role="dialog"]',
];

let supportChatInitialized = false;
let supportChatPendingOpen = false;
let supportChatPollInterval = null;
let supportChatPollAttempts = 0;
let supportChatSyncRaf = null;
let supportChatObserver = null;

function isSupportChatMobile() {
    return window.matchMedia(SUPPORT_CHAT_MOBILE_QUERY).matches;
}

function getSupportChatApi() {
    return window.Tawk_API || null;
}

function setSupportChatOpenState(isOpen) {
    document.body.classList.toggle(SUPPORT_CHAT_OPEN_CLASS, isOpen);
}

function updateSupportChatIframeClass() {
    const iframe = document.querySelector(SUPPORT_CHAT_IFRAME_SELECTOR);

    if (iframe) {
        iframe.classList.add('bottom-adjust');
    }

    return iframe;
}

function wrapSupportChatCallback(api, callbackName, handler) {
    const previousCallback = api[callbackName];

    api[callbackName] = function () {
        if (typeof previousCallback === 'function') {
            previousCallback.apply(this, arguments);
        }

        handler();
    };
}

function bindSupportChatCallbacks(api) {
    if (!api || api.__pulsGptSupportChatCallbacksBound) {
        return;
    }

    wrapSupportChatCallback(api, 'onLoad', function () {
        if (supportChatPendingOpen) {
            openSupportChat();
            return;
        }

        scheduleSupportChatSync();
    });

    wrapSupportChatCallback(api, 'onChatMaximized', function () {
        setSupportChatOpenState(true);
        scheduleSupportChatSync();
    });

    ['onChatMinimized', 'onChatHidden', 'onChatEnded'].forEach(function (callbackName) {
        wrapSupportChatCallback(api, callbackName, function () {
            setSupportChatOpenState(false);
            scheduleSupportChatSync();
        });
    });

    api.__pulsGptSupportChatCallbacksBound = true;
}

function stopSupportChatPoll() {
    if (supportChatPollInterval) {
        clearInterval(supportChatPollInterval);
        supportChatPollInterval = null;
    }
}

function startSupportChatPoll() {
    if (supportChatPollInterval) {
        return;
    }

    supportChatPollAttempts = 0;
    supportChatPollInterval = setInterval(function () {
        supportChatPollAttempts += 1;

        const hasApi = syncSupportChatVisibility();

        if (supportChatPendingOpen) {
            openSupportChat();
        }

        if ((hasApi && !supportChatPendingOpen) || supportChatPollAttempts >= 120) {
            stopSupportChatPoll();
        }
    }, 250);
}

function syncSupportChatVisibility() {
    updateSupportChatIframeClass();

    const api = getSupportChatApi();

    if (!api) {
        return false;
    }

    bindSupportChatCallbacks(api);

    if (supportChatPendingOpen) {
        openSupportChat();
        return true;
    }

    if (isSupportChatMobile()) {
        if (!document.body.classList.contains(SUPPORT_CHAT_OPEN_CLASS) && typeof api.hideWidget === 'function') {
            api.hideWidget();
        }
    } else {
        setSupportChatOpenState(false);

        if (typeof api.showWidget === 'function') {
            api.showWidget();
        }
    }

    return true;
}

function scheduleSupportChatSync() {
    if (supportChatSyncRaf) {
        return;
    }

    supportChatSyncRaf = window.requestAnimationFrame(function () {
        supportChatSyncRaf = null;
        syncSupportChatVisibility();
    });
}

function getSupportChatCookieModalElement() {
    return document.getElementById('supportChatCookieModal');
}

function showSupportChatCookieNotice() {
    const modalElement = getSupportChatCookieModalElement();

    if (!modalElement || typeof bootstrap === 'undefined') {
        return;
    }

    const fallback = modalElement.querySelector('.support-chat-cookie-fallback');

    if (fallback) {
        fallback.hidden = true;
    }

    const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
    modal.show();
}

function isVisibleElement(element) {
    if (!element) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

function getCookiePreferencesRoots() {
    const roots = COOKIE_PREFERENCES_ROOT_SELECTORS.flatMap(function (selector) {
        return Array.from(document.querySelectorAll(selector));
    }).filter(isVisibleElement);

    if (roots.length) {
        return roots;
    }

    return [document.body];
}

function getFunctionalCookieSearchText(element) {
    return [
        element.id,
        element.className,
        element.getAttribute('name'),
        element.getAttribute('value'),
        element.getAttribute('aria-label'),
        element.getAttribute('aria-controls'),
        element.getAttribute('data-cookie-consent'),
        element.getAttribute('data-cookie-consent-level'),
        element.getAttribute('data-consent-level'),
        element.getAttribute('data-category'),
        element.getAttribute('data-id'),
        element.textContent,
    ].filter(Boolean).join(' ');
}

function isFunctionalCookieCandidate(element) {
    return FUNCTIONAL_COOKIE_CATEGORY_PATTERN.test(getFunctionalCookieSearchText(element));
}

function isSafeFunctionalCookieControl(element) {
    const tagName = element.tagName.toLowerCase();
    const role = (element.getAttribute('role') || '').toLowerCase();
    const className = String(element.className).toLowerCase();

    if (['input', 'select', 'textarea', 'label'].includes(tagName)) {
        return false;
    }

    if (['checkbox', 'switch', 'radio'].includes(role)) {
        return false;
    }

    if (element.hasAttribute('aria-checked') || element.hasAttribute('aria-pressed')) {
        return false;
    }

    if (/(switch|toggle|checkbox|slider)/.test(className)) {
        return false;
    }

    return tagName === 'button'
        || tagName === 'a'
        || tagName === 'summary'
        || role === 'tab'
        || role === 'button';
}

function findFunctionalCookieControl(root) {
    const controls = Array.from(root.querySelectorAll([
        'button',
        'a',
        'summary',
        '[role="tab"]',
        '[role="button"]',
        '[data-cookie-consent-level="functionality"]',
        '[data-consent-level="functionality"]',
        '[data-category="functionality"]',
    ].join(',')));

    return controls.find(function (control) {
        return isVisibleElement(control)
            && isSafeFunctionalCookieControl(control)
            && isFunctionalCookieCandidate(control);
    });
}

function findFunctionalCookieSection(root) {
    const sections = Array.from(root.querySelectorAll([
        '[id*="functionality" i]',
        '[class*="functionality" i]',
        '[data-cookie-consent-level="functionality"]',
        '[data-consent-level="functionality"]',
        '[data-category="functionality"]',
        'section',
        'article',
        'fieldset',
        '[role="tabpanel"]',
        '[role="group"]',
    ].join(',')));

    return sections.find(function (section) {
        return isVisibleElement(section) && isFunctionalCookieCandidate(section);
    });
}

function focusFunctionalCookiePreferences() {
    const roots = getCookiePreferencesRoots();

    for (const root of roots) {
        const control = findFunctionalCookieControl(root);

        if (control) {
            control.click();
            control.focus({preventScroll: true});
        }

        const section = findFunctionalCookieSection(root);

        if (section) {
            section.scrollIntoView({block: 'center', behavior: 'smooth'});

            if (typeof section.focus === 'function') {
                section.focus({preventScroll: true});
            }

            return true;
        }

        if (control) {
            return true;
        }
    }

    return false;
}

function scheduleFunctionalCookiePreferencesFocus(attempt = 0) {
    window.setTimeout(function () {
        window.requestAnimationFrame(function () {
            if (focusFunctionalCookiePreferences() || attempt >= FUNCTIONAL_COOKIE_PREFERENCES_RETRY_LIMIT) {
                return;
            }

            scheduleFunctionalCookiePreferencesFocus(attempt + 1);
        });
    }, attempt === 0 ? 50 : FUNCTIONAL_COOKIE_PREFERENCES_RETRY_DELAY);
}

function openSupportChatCookieSettings() {
    const preferencesLink = document.getElementById('open_preferences_center');
    const modalElement = getSupportChatCookieModalElement();

    if (preferencesLink) {
        if (modalElement && typeof bootstrap !== 'undefined') {
            const modal = bootstrap.Modal.getInstance(modalElement);

            if (modal) {
                modal.hide();
            }
        }

        preferencesLink.click();
        scheduleFunctionalCookiePreferencesFocus();
        return;
    }

    if (modalElement) {
        const fallback = modalElement.querySelector('.support-chat-cookie-fallback');

        if (fallback) {
            fallback.hidden = false;
        }
    }
}

function openSupportChat() {
    updateSupportChatIframeClass();

    const api = getSupportChatApi();

    if (!api || typeof api.maximize !== 'function') {
        if (hasConsent(['functionality'])) {
            supportChatPendingOpen = true;
            startSupportChatPoll();
            return false;
        }

        supportChatPendingOpen = false;
        showSupportChatCookieNotice();
        return false;
    }

    bindSupportChatCallbacks(api);
    supportChatPendingOpen = false;
    setSupportChatOpenState(true);

    if (typeof api.showWidget === 'function') {
        api.showWidget();
    }

    window.requestAnimationFrame(function () {
        api.maximize();
    });
    setTimeout(function () {
        api.maximize();
    }, 150);

    return true;
}

function initSupportChatController() {
    if (supportChatInitialized) {
        return;
    }

    supportChatInitialized = true;
    window.PulsGptSupportChat = {
        open: openSupportChat,
        syncVisibility: scheduleSupportChatSync,
    };

    window.addEventListener('resize', scheduleSupportChatSync);
    supportChatObserver = new MutationObserver(scheduleSupportChatSync);
    supportChatObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
    });

    scheduleSupportChatSync();
    startSupportChatPoll();
}


function hideNavContainer(classNameA, classNameB) {
    const color = getCssVariableValue('--color-content-link');

    $(classNameA).css('transform', 'scaleY(0)') // '.profile-dropdown-container'
    $(classNameA).css('top', '10px')
    $(classNameB).css('color', color)
}

function showNavContainer(classNameA, classNameB) {
    const color = getCssVariableValue('--color-content-link-hover');

    $(classNameA).css('transform', 'scaleY(1)') //
    $(classNameA).css('top', '20px')
    $(classNameB).css('color', color)
}

function hideNavContainerN(classNameA, classNameB) {

    $(classNameA).removeClass('visible-element') // '.profile-dropdown-container'
    $(classNameA).addClass('hidden-element') // '.profile-dropdown-container'
    $(classNameA).css('top', '10px')
    $(classNameB).css('color', '#ffffff')
}

function showNavContainerN(classNameA, classNameB) {

    $(classNameA).removeClass('hidden-element')
    $(classNameA).addClass('visible-element') //
    $(classNameA).css('top', '20px')
    $(classNameB).css('color', '#aebee7')
}


export function handleScrollButtons() {

    appendScrollButtons()

    function hideButtons() {
        $('#scroll-up, #scroll-down').hide();
    }

    let lastScrollTop = 0;
    let scrollTimeout;

    const debounceThreshold = 71;
    let lastDirection = null;

    // Listen to the scroll event
    $(window).scroll(function () {
        const threadsInitialAutoscrollState = window.__threadsInitialAutoscrollState;
        if (threadsInitialAutoscrollState === 'pending' || threadsInitialAutoscrollState === 'animating') {
            $('.app-header:not(.app-header-absolute):not(.app-header-sticky)').css('top', '0');
            lastScrollTop = $(this).scrollTop();
            return;
        }

        // Get current scroll top position
        let currentScrollTop = $(this).scrollTop();

        clearTimeout(scrollTimeout);


        let windowHeight = $(window).height();
        let documentHeight = $(document).height();

        let currentDirection = currentScrollTop > lastScrollTop ? 'down' : 'up';

        // Проверяем, если направление изменилось и пройден порог дебаунса
        if (Math.abs(currentScrollTop - lastScrollTop) > debounceThreshold) {
            if (currentDirection === 'down') {
                // Скроллим вниз - показываем кнопку "вверх"
                if (currentScrollTop + windowHeight < documentHeight) {
                    $('#scroll-up').hide(); // Скрыть кнопку "вверх"
                    $('#scroll-down').show(); // Показать кнопку "вниз"
                }
            } else if (currentDirection === 'up') {
                // Скроллим вверх - показываем кнопку "вниз"
                if (currentScrollTop > 0) {
                    $('#scroll-down').hide(); // Скрыть кнопку "вниз"
                    $('#scroll-up').show(); // Показать кнопку "вверх"
                }
            }

            // Обновляем последнее направление
            lastDirection = currentDirection;
        }

        if (currentScrollTop === 0) {
            $('#scroll-up').hide();
        }

        // Проверяем, находимся ли мы в самом конце страницы
        if (currentScrollTop + windowHeight >= documentHeight) {
            $('#scroll-down').hide();
        }

        scrollTimeout = setTimeout(hideButtons, 1600);

        // Set the last scroll position to the current scroll position
        lastScrollTop = currentScrollTop;

    });
    $('#scroll-up').click(function () {
        $('html, body').animate({scrollTop: 0}, 200);
        return false;
    });

    $('#scroll-down').click(function () {
        $('html, body').animate({scrollTop: $(document).height()}, 200);
        return false;
    });

}


export function handleCollapseAllButton() {
    appendCollapseAllButton()
    let lastScrollTop = 0;
    let scrollTimeout;

    const debounceThreshold = 71;

    // Listen to the scroll event
    $(window).scroll(function () {
        // Get current scroll top position
        let currentScrollTop = $(this).scrollTop();

        clearTimeout(scrollTimeout);

        // Проверяем, если направление изменилось и пройден порог дебаунса
        if (Math.abs(currentScrollTop - lastScrollTop) > debounceThreshold) {

            if (checkForCollapsibles()) {
                $('#collapse-all').fadeIn(300); // Показать кнопку, если есть элементы
            } else {
                $('#collapse-all').fadeOut(300); // Скрыть кнопку, если нет элементов
            }

        }

        scrollTimeout = setTimeout(hideCollapseButton, 1600);

        // Set the last scroll position to the current scroll position
        lastScrollTop = currentScrollTop;


    });
}

function handleVideoModal() {
    document.querySelectorAll('.videomodal').forEach(modal => {
        // Добавляем обработчик события закрытия для каждого модального окна
        modal.addEventListener('hidden.bs.modal', function () {
            // Находим видео внутри текущего модального окна
            const video = modal.querySelector('video');

            // Если видео найдено, ставим его на паузу и сбрасываем время
            if (video) {
                video.pause();
            }
        });
    });

    document.querySelectorAll('.video-container').forEach(container => {
        const video = container.querySelector('video');
        const poster = container.querySelector('.video-poster');

        // Обработка клика на постере
        poster.addEventListener('click', () => {
            // Проверяем, что видео на паузе и постер виден
            if (video.paused && !container.classList.contains('playing')) {
                container.classList.add('playing');
                video.play();
            }
        });

        // Скрытие постера при воспроизведении
        video.addEventListener('play', () => {
            container.classList.add('playing');
        });

        // Возврат к постеру при остановке
        video.addEventListener('pause', () => {
            if (video.currentTime === 0 || video.ended) {
                container.classList.remove('playing');
            }
        });

        video.addEventListener('ended', () => {
            container.classList.remove('playing');
        });
    });

}


async function updateCardsModal(modal) {
    let spinner = modal.find('.spinner-container');
    let footer = modal.find('.modal-footer');
    let description = modal.find('#manageSavedCardsModalDescription');
    description.hide()
    //footer.hide()

    spinner.show();

    getUserCards().then(cardsData => {
        spinner.hide();
        console.log("cardsData", cardsData)
        const cardsHTML = generateCreditCardsHTML(cardsData)
        description.empty();
        description.append(cardsHTML);
        bindToolTips(".card-tooltip")
        description.show();
        footer.show();
    }).catch(() => {
            if (description.length) {
                description.show()
                footer.show();
            }
        }
    )
}


function handle_modal_url() {
    const openModalById = (modalId) => {
        const modalElement = document.getElementById(modalId);
        if (modalElement) {
            const modal = new bootstrap.Modal(modalElement);
            modal.show();

            // Очищаем параметр из URL после открытия
            const url = new URL(window.location);
            url.searchParams.delete('modal');
            history.replaceState({}, '', url);
        }
    };

    // Парсим URL и проверяем наличие параметра 'modal'
    const urlParams = new URLSearchParams(window.location.search);
    const modalId = urlParams.get('modal');

    // Если параметр есть и он не пустой
    if (modalId) {
        // Открываем модальное окно сразу
        openModalById(modalId);

        // Дополнительная обработка для закрытия через кнопку "Назад"
        window.addEventListener('popstate', () => {
            const currentModal = bootstrap.Modal.getInstance(document.getElementById(modalId));
            if (currentModal) {
                currentModal.hide();
            }
        });
    }
}

export function handleEvents() {

    let lastScrollTop = 0;
    const body = $('body');
    let addCardController = null;

    initSupportChatController();

    // Listen to the scroll event
    $(window).scroll(function () {
        // Get current scroll top position
        let currentScrollTop = $(this).scrollTop();

        if (currentScrollTop > lastScrollTop) {
            // Scrolling down
            $('.app-header:not(.app-header-absolute):not(.app-header-sticky)').css('top', '-72px')
        } else if (currentScrollTop < lastScrollTop) {
            $('.app-header:not(.app-header-absolute):not(.app-header-sticky)').css('top', '0')
        }

        lastScrollTop = currentScrollTop;


    });


    body.on("mouseover", '.header-profile-nav-container', function (event) {
        clearTimeout(headerConfig.navMenuTimeout)
        showNavContainer('.profile-dropdown-container', '.profile-nav-link')

    })


    body.on("mouseover", '.profile-dropdown-container', function (event) {
        clearTimeout(headerConfig.navMenuTimeout)
        showNavContainer('.profile-dropdown-container', '.profile-nav-link')

    })


    body.on("mouseout", '.header-profile-nav-container', function (event) {
        headerConfig.navMenuTimeout = setTimeout(() => hideNavContainer('.profile-dropdown-container', '.profile-nav-link'), 1000)

    })
    body.on("mouseout", '.profile-dropdown-container', function (event) {
        headerConfig.navMenuTimeout = setTimeout(() => hideNavContainer('.profile-dropdown-container', '.profile-nav-link'), 1000)

    })

    body.on("mouseover", '.language-nav-container,.language-dropdown-container', function (event) {

        clearTimeout(headerConfig.langMenuTimeout)
        showNavContainer('.language-dropdown-container', '.language-nav-text')

    })


    body.on("mouseout", '.language-nav-container', function (event) {
        headerConfig.langMenuTimeout = setTimeout(() => hideNavContainer('.language-dropdown-container', '.language-nav-text'), 1000)

    })
    body.on("mouseout", '.language-dropdown-container', function (event) {
        headerConfig.langMenuTimeout = setTimeout(() => hideNavContainer('.language-dropdown-container', '.language-nav-text'), 1000)

    })

    body.on("click", '.nav_burger_container', function (event) {
        $('.nav_burger, .profile-mobile-container').toggleClass('active')
    })

    body.on("click", '.js-open-support-chat', function (event) {
        event.preventDefault();
        $('.nav_burger, .profile-mobile-container').removeClass('active')
        openSupportChat();
    })

    body.on("click", '.js-open-support-chat-cookie-settings', function (event) {
        event.preventDefault();
        openSupportChatCookieSettings();
    })

    body.on('show.bs.collapse', function (event) {
        const accordionElem = $(event.target);
        if (accordionElem.is('[data-account-collapse]')) {
            return;
        }
        const buttonElem = accordionElem.closest('.group-answer-row').find('.icon-btn-0');
        buttonElem.addClass('transform');
        buttonElem.closest('.group-answer-row').addClass('mb-5');
    });

    body.on('hide.bs.collapse', function (event) {
        const accordionElem = $(event.target);
        if (accordionElem.is('[data-account-collapse]')) {
            return;
        }
        const buttonElem = accordionElem.closest('.group-answer-row').find('.icon-btn-0');
        buttonElem.removeClass('transform');
        buttonElem.closest('.group-answer-row').removeClass('mb-5');
    });

    body.on('click', '#cancelSubscriptionButton', function (event) {
        const subscriptionId = $(this).data('subscription-id');


        const data = JSON.stringify({
            subscription_id: subscriptionId,
        })

        fullFetch({
            url: urls.subscribeCancel,
            method: 'POST',
            data: data,

        }).then(res => {
            const modalElement = document.getElementById('cancelSubscriptionModal');
            const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
            modal.hide()
        })
    })


    handleVideoModal();


    $('#manageSubscriptionModal').on('show.bs.modal', function (event) {

        let modal = $(this);
        modal.find(".modal-title").focus()
        let spinner = modal.find('.spinner-container');
        let footer = modal.find('.modal-footer');
        let description = modal.find('#manageSubscriptionModalBody');

        spinner.show();

        getBillingData().then(billingData => {

            spinner.hide();
            const billingHTML = renderBillingHtml(billingData);
            description.empty();
            description.append(billingHTML);
            description.show();

            setSubscribeBtnText(billingData);

            footer.show();

        }).catch(error => {
            spinner.hide();
            description.empty();
            description.append(`<div class="text-danger">${i18next.t('common:dom.billingLoadError', 'Unable to load billing data')}</div>`);
            description.show();
            footer.hide();
            console.error(error);
        })

    })

    $('#confirmDeleteCardBtn').on('click', function (event) {
        const $btn = $(this);

        // Блокируем кнопку и показываем индикатор загрузки
        $btn.prop('disabled', true);
        const originalText = $btn.html();
        $btn.html(`
        <span class="spinner-border spinner-border-sm text-light" role="status" aria-hidden="true"></span>
        ${$btn.text().trim()}
    `);

        const cardId = $btn.data('card-id');
        const data = JSON.stringify({card_id: cardId});

        fullFetch({
            url: urls.deleteUserCard,
            method: 'POST',
            data: data,
        }).then(res => {
            displaySuccess(i18next.t('common:dom.paymentMethodDeleted', 'Payment method deleted'));
            const modalElement = document.getElementById('deleteCardModal');
            const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
            modal.hide();

            // Восстанавливаем состояние кнопки после скрытия модального окна
            $btn.html(originalText);
        }).catch(() => {
            // Разблокируем кнопку при ошибке
            $btn.prop('disabled', false);
            $btn.html(originalText);
        });
    });


    body.on('click', '#add-more-card', function (event) {

        console.log("#add-more-card")

        const $btn = $(this);
        const originalHtml = $btn.html();

        // Блокируем кнопку и показываем индикатор загрузки
        $btn.prop('disabled', true);

        $btn.html(`
        <span class="spinner-border spinner-border text-dark" role="status" aria-hidden="true"></span>
    `);


        const headers = {
            'mode': 'no-cors',
        }

        const controller = new AbortController();
        addCardController = controller;

        fullFetch({
            url: urls.addUserCard,
            method: 'POST',
            headers: headers,
            signal: controller.signal,
        }).then(res => {
            if (controller.signal.aborted) return;

            if (res.url) {
                window.location.href = res.url
            }

        }).catch(error => {
            //spinner.hide()
            if (controller.signal.aborted) return;
            console.error(error);
            displayError(error);
        }).finally(() => {
            addCardController = null;
            $btn.prop('disabled', false).html(originalHtml);
        });
    });

    body.on('click', '.set-card-defaultO', function (e) {
        const $btn = $(this);

        // Блокируем кнопку и показываем индикатор загрузки
        $btn.prop('disabled', true);
        const originalText = $btn.html();
        $btn.html(`
        <span class="spinner-border spinner-border-sm text-light" role="status" aria-hidden="true"></span>
        ${$btn.text().trim()}
    `);

        const cardId = $btn.data('card-id');
        const data = JSON.stringify({card_id: cardId});

        fullFetch({
            url: urls.setUserCardDefault,
            method: 'POST',
            data: data,
        }).then(res => {
            displaySuccess("");

            updateCardsModal($('#manageSavedCardsModal')).then(r => {
            })
        }).catch(() => {
            // Разблокируем кнопку при ошибке
            $btn.prop('disabled', false);
            $btn.html(originalText);
        });

    })

    body.on('click', '.set-card-default', function (e) {
        const $btn = $(this);
        const cardId = $btn.data('card-id');
        const modal = $('#manageSavedCardsModal');
        const modalBody = modal.find('#manageSavedCardsModalBody');

        // Находим связанные элементы
        const $cardItem = $btn.closest('.card-item');
        const $rootCardElement = $btn.closest('.col-12.mb-2');
        const $currentDefaultCard = modal.find('.card-item.border-dark').closest('.col-12.mb-2');

        // Сохраняем текущее состояние для возможного отката
        const originalNewCardHTML = $rootCardElement.html();
        const originalDefaultCardHTML = $currentDefaultCard.length ? $currentDefaultCard.html() : null;
        const originalButtonHTML = $btn.html();

        // Оптимистичное обновление UI
        // 1. Обновляем новую карту (делаем ее по умолчанию)
        $cardItem.addClass('border-dark');
        $cardItem.find('.card-state-switch').addClass('disabled');
        $cardItem.find('.card-state').prop('disabled', true);

        // Добавляем бейдж "По умолчанию"
        const defaultCardTitle = i18next.t('common:dom.defaultCardTitle', 'Default');
        const defaultCardTooltip = i18next.t('common:dom.defaultCardTooltip', 'Default');

        $cardItem.find('.card-badge-container').append(`
        <span class="badge text-uppercase bg-light text-dark ml-2 card-tooltip" 
              data-bs-toggle="tooltip" data-bs-placement="top" 
              title="${defaultCardTooltip}">
            ${defaultCardTitle}
        </span>
    `);

        // Обновляем кнопки (заменяем выпадающее меню на иконку удаления)
        const deleteCardTooltipTitle = i18next.t('common:dom.deleteCardTooltipTitle', 'Delete');
        $cardItem.find('.dropdown-center').replaceWith(`
        <button class="card-button"
                data-card-id="${cardId}"
                data-masked-pan="${$rootCardElement.data('masked-pan')}"
                data-bs-target="#deleteCardModal"
                data-bs-toggle="modal"
                data-bs-dismiss="modal"
                title="${deleteCardTooltipTitle}">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon-svg" version="1.1" width="25" height="25" x="0" y="0" viewBox="0 0 24 24" xml:space="preserve"><g><g fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"><path d="M5.293 5.293a1 1 0 0 1 1.414 0l12 12a1 1 0 0 1-1.414 1.414l-12-12a1 1 0 0 1 0-1.414z" fill="currentColor" opacity="1" data-original="currentColor"></path><path d="M18.707 5.293a1 1 0 0 0-1.414 0l-12 12a1 1 0 1 0 1.414 1.414l12-12a1 1 0 0 0 0-1.414z" fill="currentColor" opacity="1" data-original="currentColor"></path></g></g></svg>
        </button>
    `);

        // 2. Обновляем старую карту по умолчанию (если она есть)
        if ($currentDefaultCard.length) {
            const $oldCardItem = $currentDefaultCard.find('.card-item');

            // Убираем стили "по умолчанию"
            $oldCardItem.removeClass('border-dark');
            $oldCardItem.find('.card-state-switch').removeClass('disabled');
            $oldCardItem.find('.card-state').prop('disabled', false);

            // Удаляем бейдж
            $oldCardItem.find('.badge.text-uppercase').remove();

            // Восстанавливаем выпадающее меню
            const setAsDefaultTitle = i18next.t('common:dom.setCardAsDefaultTitle', 'Set as default');
            const deleteCard = i18next.t('common:dom.deleteCard', 'Delete');

            $oldCardItem.find('.card-button.card-tooltip').replaceWith(`
            <div class="dropdown-center">
                <button class="card-button" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                   <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="15" height="15" x="0" y="0" viewBox="0 0 426.667 426.667" class="icon-svg" xml:space="preserve"><g><circle cx="42.667" cy="213.333" r="42.667" fill="currentColor" opacity="1" data-original="currentColor"></circle><circle cx="213.333" cy="213.333" r="42.667" fill="currentColor" opacity="1" data-original="currentColor"></circle><circle cx="384" cy="213.333" r="42.667" fill="currentColor" opacity="1" data-original="currentColor"></circle></g></svg>
                </button>
                <ul class="dropdown-menu">
                    <li>
                        <button class="dropdown-item set-card-default" 
                                data-card-id="${$currentDefaultCard.data('card-id')}">
                            ${setAsDefaultTitle}
                        </button>
                    </li>
                    <li>
                        <button class="dropdown-item" 
                                data-card-id="${$currentDefaultCard.data('card-id')}"
                                data-masked-pan="${$currentDefaultCard.data('masked-pan')}"
                                data-bs-target="#deleteCardModal"
                                data-bs-toggle="modal" 
                                data-bs-dismiss="modal">
                            ${deleteCard}
                        </button>
                    </li>
                </ul>
            </div>
        `);
        }

        // Обновляем tooltips
        bindToolTips(".card-tooltip");

        // Блокируем кнопку и показываем спиннер
        $btn.prop('disabled', true).html(`
        <span class="spinner-border spinner-border-sm text-light" role="status" aria-hidden="true"></span>
        ${$btn.text().trim()}
    `);

        // Отправляем запрос на сервер
        fullFetch({
            url: urls.setUserCardDefault,
            method: 'POST',
            data: JSON.stringify({card_id: cardId}),
        }).then(res => {
            displaySuccess("");
            // При успехе ничего не делаем - UI уже обновлен
        }).catch(() => {
            // Откатываем изменения при ошибке
            $rootCardElement.html(originalNewCardHTML);
            if ($currentDefaultCard.length && originalDefaultCardHTML) {
                $currentDefaultCard.html(originalDefaultCardHTML);
            }
            bindToolTips(".card-tooltip");
        }).finally(() => {
            // Разблокируем кнопку
            $btn.prop('disabled', false).html(originalButtonHTML);
        });
    });

    const manageSavedCardsModal = $('#manageSavedCardsModal');

    manageSavedCardsModal.on('show.bs.modal', function (event) {
        $(this).find(".modal-title").focus()
        updateCardsModal($(this)).then(r => {
        })

    });

    manageSavedCardsModal.on('hide.bs.modal', function (event) {
        if (addCardController) {
            addCardController.abort();        // отменяем запрос
            addCardController = null;
        }
    })

    const deleteCardModal = $('#deleteCardModal')

    deleteCardModal.on('show.bs.modal', function (event) {
        const targetBtn = $(event.relatedTarget)

        const cardId = targetBtn.data('card-id')
        const maskedPan = targetBtn.data('masked-pan')


        const confirmDeleteBtn = document.getElementById('confirmDeleteCardBtn');

        confirmDeleteBtn.dataset.cardId = cardId;

        const maskedPanContainer = $("#deleteCardMaskedPan")

        maskedPanContainer.text(maskedPan)

        const modal = $(this)


    })

    deleteCardModal.on('hide.bs.modal', function (event) {

        const manageSavedCardsModal = document.querySelector('#manageSavedCardsModal')
        const modal = bootstrap.Modal.getOrCreateInstance(manageSavedCardsModal)
        modal.show()


    })


    $('#cancelSubscriptionModal').on('hide.bs.modal', function (event) {

        const manageSubscriptionModal = document.querySelector('#manageSubscriptionModal')
        const modal = bootstrap.Modal.getOrCreateInstance(manageSubscriptionModal)
        modal.show()


    })


    body.on('click', '.card-state-switch', function (e) {
        // this === checkbox
        const $el = $(this);
        const $cb = $el.find('.card-state');

        const oldVal = $cb.prop('checked');   // ← состояние ДО клика
        const cardId = $cb.data('card-id');

        // Отменяем реальное переключение чек-бокса:
        e.preventDefault();                    // чек-бокс НЕ изменится визуально

        // Желаемое новое состояние ─ противоположное текущему
        const newVal = !oldVal;

        const payload = JSON.stringify({
            card_id: cardId,
            is_active: newVal,
        });

        fullFetch({
            url: urls.setUserCardState,
            method: 'POST',
            data: payload,
        })
            .then(() => {
                // ✓ бек вернул success – теперь меняем чек-бокс «по-настоящему»
                $cb.prop('checked', newVal);
                // здесь можно показать тост / уведомление
            })
            .catch(err => {
                console.error(err);
                displayError(err);
                // чек-бокс уже остался в старом состоянии, ничего откатывать не нужно
            });
    });

    handle_modal_url()
}
